import * as React from 'react'
import Loader from 'react-loader-spinner'

import InfiniteList from '@components/infiniteList/infiniteList'
import Searchbar from '@components/searchbar/searchbar'
import Select from '@components/select/select'

import Theme from '@theme/Theme'
import styled from '@theme/styled-components'

import useI18n from '@store/i18n/useI18n'

interface Props {
  placeholder: string
  setSelectOpen: (open: boolean) => void
  regionList: Option[]
  selectedRegion: Option
  setSelectedRegion: (region: Option) => void
  txt: string
  setText: (txt: string) => void
  screenStatus: ScreenStatus
  businesses: Option[]
  selected: Option[]
  setSelected: (selection: Option[]) => void
  setDeclarationLabel?: (label: string) => void
  singleSelect?: boolean
}

const BusinessSelectModal = ({
  placeholder,
  setSelectOpen,
  regionList,
  selectedRegion,
  setSelectedRegion,
  txt,
  setText,
  screenStatus,
  businesses,
  selected,
  setSelected,
  setDeclarationLabel,
  singleSelect,
}: Props) => {
  const i18n = useI18n()

  return (
    <Background>
      <SelectorContent onClick={() => null}>
        <ScreenTitleContainer>
          <ScreenTitle>{placeholder}</ScreenTitle>
          <Back onClick={() => setSelectOpen(false)}>{i18n.t('components.menu.back')}</Back>
        </ScreenTitleContainer>
        <OptionList>
          <SearchContainer>
            <SelectorTitle>{i18n.t('components.adminCompanies.subtitle.searchbar')}</SelectorTitle>
            <Searchbar placeholder={i18n.t('components.menu.searchbar')} text={txt} setText={(e) => setText(e)} />
          </SearchContainer>
          <SearchContainer>
            <SelectorTitle>{i18n.t('components.adminCompanies.subtitle.regionpicker')}</SelectorTitle>
            <Select
              options={regionList}
              selected={selectedRegion}
              setSelected={(selected) => selected && setSelectedRegion(selected)}
              placeholder={i18n.t('components.adminCompanies.placeholder02')}
              isClearable={false}
              noMargin
            />
          </SearchContainer>
          {screenStatus === 'loading' ? (
            <StatusContainer>
              <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={10} />
            </StatusContainer>
          ) : (
            <>
              {businesses.length === 0 ? (
                <NoOption>{i18n.t('components.menu.noChantier')}</NoOption>
              ) : (
                <InfiniteList
                  data={businesses}
                  height={100}
                  renderItem={(opt) => {
                    const itemSelected = !!selected.find((b) => b.value === opt.value)

                    return (
                      <BusinessOptionContainer
                        height={100}
                        selected={itemSelected}
                        onClick={() => {
                          if (singleSelect) {
                            setSelected([opt])
                            !!setDeclarationLabel && setDeclarationLabel(opt.label)
                            setSelectOpen(false)
                          } else {
                            itemSelected
                              ? setSelected(selected.filter((s) => s.value !== opt.value))
                              : setSelected([...selected, opt])
                          }
                        }}>
                        <BusinessOptionLetter selected={itemSelected}>
                          <LettersName>{opt.label.slice(0, 2)}</LettersName>
                        </BusinessOptionLetter>
                        <BusinessOptionLabel>
                          <BusinessName>{opt.label}</BusinessName>
                        </BusinessOptionLabel>
                      </BusinessOptionContainer>
                    )
                  }}
                />
              )}
            </>
          )}
        </OptionList>
      </SelectorContent>
    </Background>
  )
}
export default BusinessSelectModal

const Background = styled('div')`
  background-color: ${(props) => props.theme.colors.backgroundColor};
  position: absolute;
  cursor: pointer;
  z-index: 1;
  bottom: 0;
  right: 0;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  color: red;
  cursor: pointer;
  font-weight: bold;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  padding: 15px;

  display: flex;
  align-items: center;
  justify-content: space-between;
`
const SelectorContent = styled('div')`
  flex: 1;
  z-index: 2;
  display: flex;
  height: 750px;
  padding: 10px;
  max-width: 730px;
  border-radius: 20px;
  flex-direction: column;
  background-color: white;
  box-shadow: 0px 0px 15px ${(props) => props.theme.colors.menuShadow};
`
const OptionList = styled('div')`
  flex: 1;
  display: flex;
  overflow: scroll;
  flex-direction: column;
`
const NoOption = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  justify-content: center;
  align-items: center;
  text-align: center;
  display: flex;
  flex: 1;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  font-size: 13px;
`
const SearchContainer = styled('div')`
  padding: 10px 15px;
`
const BusinessOptionContainer = styled('div')<{ height: number; selected: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 5px 25px 4px 25px;
  height: ${(props) => props.height - 10}px;
  border: 0px solid #f1f1f1;
  border-bottom-width: 1px;
  &: hover {
    background-color: ${(props) => props.theme.colors.filterBackgroundColor};
  }
  ${(props) => props.selected && `border-color: ${props.theme.colors.primary_green};`}
`
const BusinessOptionLetter = styled('div')<{ selected: boolean }>`
  background-color: ${(props) =>
    !props.selected ? props.theme.colors.primary_blue : props.theme.colors.primary_green};
  height: 60px;
  width: 60px;
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
`
const LettersName = styled('div')`
  ${(props) => props.theme.fonts.calendarLibelle};
  color: ${(props) => props.theme.colors.secondary_text};
  text-align: center;
`
const BusinessOptionLabel = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
`
const BusinessName = styled('div')`
  font-family: Avenir;
  font-style: normal;
  font-weight: 900;
  font-size: 17px;
  line-height: 21px;
  color: ${(props) => props.theme.colors.libelleColor};
`
const SelectorTitle = styled('div')`
  font-family: Avenir;
  font-style: normal;
  font-weight: 900;
  font-size: 14px;
  line-height: 19px;
  color: ${(props) => props.theme.colors.markerEnded};
`
